<template>
  <a
    v-bind="$attrs"
    :tabIndex="disabled ? null : 0"
    ref="container"
    :rel="rel"
    :target="target"
    :data-disabled="disabled"
    :data-selected="selected"
    :href="disabled ? null : href"
    @click="onClick"
    @keydown="onKeydown"
    @keyup="onKeyup"
    :class="$style.container"
    :style="style.container"
  >
    <div
      :data-disabled="disabled"
      :data-selected="selected"
      :class="$style.inner"
      :style="style.inner"
    >
      <template v-if="truncate">
        <div :style="truncateStyle">
          <slot />
        </div>
      </template>
      <template v-else>
        <slot />
      </template>
    </div>
  </a>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';

import { createStyle } from '@v1/composables/create-style';
import { StyleExtender } from '@v1/composables/style-extender';
import { useKeyboard } from '@v1/composables/use-keyboard';
import { useTruncateStyle } from '@v1/composables/use-styles';

const styleExtender = new StyleExtender({
  variablePrefix: '--v1-base-link',
  extraVariablePostfixes: ['selected', 'focusVisible'],
});

const router = useRouter();

const props = defineProps({
  alignCenter: {
    type: Boolean,
    required: false,
    default: false,
  },
  disabled: {
    type: Boolean,
    required: false,
    default: false,
  },
  selected: {
    type: Boolean,
    required: false,
    default: false,
  },
  disableStopPropagation: {
    type: Boolean,
    required: false,
    default: false,
  },
  disableTruncate: {
    type: Boolean,
    required: false,
    default: false,
  },
  href: {
    type: [Object, String],
    required: false,
    default: null,
  },
  inline: {
    type: Boolean,
    required: false,
    default: false,
  },
  inlineBlock: {
    type: Boolean,
    required: false,
    defualt: false,
  },
  openInCurrentTab: {
    type: Boolean,
    required: false,
    default: false,
  },
  to: {
    type: Object,
    required: false,
    default: null,
  },
  styleExtension: {
    type: Object,
    required: false,
    default: () => ({}),
  },
  asReactive: {
    type: Boolean,
    required: false,
    default: false,
  },
});

// computed
const rel = computed(() => (props.openInCurrentTab ? null : 'noopener noreferrer'));
const target = computed(() => (props.openInCurrentTab ? '_self' : '_blank'));
const truncate = computed(() => (
  !props.disableTruncate
  && !props.inline
  && !props.inlineBlock));

// data
const truncateStyle = ref({
  ...useTruncateStyle(),
  ...(props.alignCenter ? {
    alignItems: 'center',
    display: 'flex',
  } : {}),
});

const container = ref<HTMLElement | null>(null);

const style = createStyle({
  container: {},
  inner: {},
}, {
  asReactive: props.asReactive,
});

styleExtender.extend({
  target: style.container,
  extension: props.styleExtension.container,
  selector: 'container',
});

styleExtender.extend({
  target: style.inner,
  extension: props.styleExtension.inner,
  selector: 'inner',
});

if (props.inline || props.inlineBlock) {
  style.container.display = props.inline ? 'inline' : 'inline-block';
  style.container.verticalAlign = 'top';

  if (style.inner.display === 'flex') {
    // Do not use inline-block. 'inline-block' disallow wrapping text
    // and it's not expected behavior as inline element.
    style.inner.display = 'inline';
  }
}
// methods
const onClick = (e: MouseEvent) => {
  if (!props.disableStopPropagation) e.stopPropagation();
  if (!props.disabled && props.to) router.push(props.to);
  container.value.blur();
};

const clickByKeydown = (e: KeyboardEvent): void => {
  if (!props.disableStopPropagation) e.stopPropagation();
  if (!props.disabled && props.to) router.push(props.to);
  if (!props.disabled && props.href) container.value.click();
  container.value.blur();
};

const { onKeydown, onKeyup } = useKeyboard({
  keydownCallbacks: {
    ' ': clickByKeydown,
    Enter: clickByKeydown,
  },
});
</script>

<style module lang="scss">
.container {
  background-color: var(--v1-base-link-container-background-color);
  border: var(--v1-base-link-container-border);
  border-radius: var(--v1-base-link-container-border-radius);
  border-width: var(--v1-base-link-container-border-width);
  box-shadow: var(--v1-base-link-container-box-shadow);
  cursor: var(--v1-base-link-container-cursor);

  &[data-disabled=true] {
    background-color: var(--v1-base-link-container-background-color-disabled);
    border: var(--v1-base-link-container-border-disabled);
    border-radius: var(--v1-base-link-container-border-radius-disabled);
    border-width: var(--v1-base-link-container-border-width-disabled);
    box-shadow: var(--v1-base-link-container-box-shadow-disabled);
    cursor: var(--v1-base-link-container-cursor-disabled);
  }

  &[data-disabled=false] {
    &[data-selected=true] {
      background-color: var(--v1-base-link-container-background-color-selected);
      border: var(--v1-base-link-container-border-selected);
      border-radius: var(--v1-base-link-container-border-radius-selected);
      border-width: var(--v1-base-link-container-border-width-selected);
      box-shadow: var(--v1-base-link-container-box-shadow-selected);
      cursor: var(--v1-base-link-container-cursor-selected);
    }

    &[data-selected=false] {
      &:hover {
        background-color: var(--v1-base-link-container-background-color-hover);
        border: var(--v1-base-link-container-border-hover);
        border-radius: var(--v1-base-link-container-border-radius-hover);
        border-width: var(--v1-base-link-container-border-width-hover);
        box-shadow: var(--v1-base-link-container-box-shadow-hover);
        cursor: var(--v1-base-link-container-cursor-hover);
      }
      &:active,
      &:focus-visible {
        background-color: var(--v1-base-link-container-background-color-active);
        border: var(--v1-base-link-container-border-active);
        border-radius: var(--v1-base-link-container-border-radius-active);
        border-width: var(--v1-base-link-container-border-width-active);
        box-shadow: var(--v1-base-link-container-box-shadow-active);
        cursor: var(--v1-base-link-container-cursor-active);
        outline: none;
        outline-color: none;
        outline-width: none;
      }
      &:focus-visible {
        outline: var(--v1-base-link-container-outline-active);
        outline-color: var(--v1-base-link-container-outline-color-active);
        outline-width: var(--v1-base-link-container-outline-width-active);
      }
    }
  }
}

.inner {
  color: var(--v1-base-link-inner-color);
  font-family: var(--v1-base-link-inner-font-family);
  font-size: var(--v1-base-link-inner-font-size);
  font-weight: var(--v1-base-link-inner-font-weight);
  text-decoration: var(--v1-base-link-inner-text-decoration);

  &[data-disabled=true] {
    color: var(--v1-base-link-inner-color-disabled);
    font-family: var(--v1-base-link-inner-font-family-disabled);
    font-size: var(--v1-base-link-inner-font-size-disabled);
    font-weight: var(--v1-base-link-inner-font-weight-disabled);
    text-decoration: var(--v1-base-link-inner-text-decoration-disabled);
  }

  &[data-disabled=false] {
    &[data-selected=true] {
      color: var(--v1-base-link-inner-color-selected);
      font-family: var(--v1-base-link-inner-font-family-selected);
      font-size: var(--v1-base-link-inner-font-size-selected);
      font-weight: var(--v1-base-link-inner-font-weight-selected);
      text-decoration: var(--v1-base-link-inner-text-decoration-selected);
    }

    &[data-selected=false] {
      &:hover {
        color: var(--v1-base-link-inner-color-hover);
        font-family: var(--v1-base-link-inner-font-family-hover);
        font-size: var(--v1-base-link-inner-font-size-hover);
        font-weight: var(--v1-base-link-inner-font-weight-hover);
        text-decoration: var(--v1-base-link-inner-text-decoration-hover);
      }

      &:active,
      &:focus-visible {
        color: var(--v1-base-link-inner-color-active);
        font-family: var(--v1-base-link-inner-font-family-active);
        font-size: var(--v1-base-link-inner-font-size-active);
        font-weight: var(--v1-base-link-inner-font-weight-active);
        text-decoration: var(--v1-base-link-inner-text-decoration-active);
      }
    }
  }
}
</style>
